/**
 * Keeps a tooltip visible when its anchor lives inside a CDK overlay.
 *
 * Since the Angular 21 upgrade, `createOverlayRef` attaches every overlay with `popover="manual"`, which
 * puts dialogs, menus, selects and `cdkConnectedOverlay` panels in the browser's **top layer**. The top
 * layer paints above the entire normal layer, so a tippy tooltip appended to `document.body` renders
 * *behind* the panel it is anchored in — whatever z-index either one carries. Reported on the saved-views
 * picker, where the share tooltip was painted behind the popover.
 *
 * Neither of the obvious fixes works. z-index cannot reach into the top layer at all. `appendTo` moves the
 * tooltip inside the panel, where the UA's `[popover] { overflow: auto }` clips it the moment it extends
 * past the panel's box — which is exactly where a tooltip sits. What is left is to join the top layer,
 * where ordering is by promotion order, so a tooltip promoted on mount always paints over its own overlay.
 *
 * Wire it in once per app via `provideTippyConfig({ onMount })` — helipopper's own `onMount` calls
 * `globalConfig.onMount`, so this reaches every tooltip without touching a single template. The visual
 * resets that go with promotion live in `shared-styles/tippy-top-layer.css`.
 */

/** Minimal shape of the tippy instance used here; avoids pulling tippy's types into every app module. */
interface TippyLikeInstance {
  popper: HTMLElement;
  reference: Element;
}

/** Marks a promoted tooltip so the stylesheet can undo the UA's `[popover]` defaults. */
export const TIPPY_TOP_LAYER_CLASS = 'tippy-top-layer';

/**
 * Promotes [instance]'s tooltip into the top layer, but **only** when it is anchored inside an
 * already-open popover. A tooltip anywhere else is left untouched: the overwhelming majority are not
 * inside an overlay and render correctly today, and promoting those would change stacking app-wide to
 * fix a problem they do not have.
 */
export function promoteTooltipAboveCdkOverlay(instance: TippyLikeInstance): void {
  const popper = instance?.popper;
  const reference = instance?.reference;
  if (!popper || !reference) {
    return;
  }

  // Feature-detect FIRST. `:popover-open` is an invalid selector where the Popover API is unsupported, and
  // `closest` THROWS on an invalid selector rather than returning null — which would break every tooltip
  // in the app, not just the ones in overlays. This guard also covers jsdom, which implements none of it.
  if (typeof popper.showPopover !== 'function') {
    return;
  }

  // Only act when the anchor sits inside an open top-layer popover, i.e. a CDK overlay.
  if (!reference.closest(':popover-open')) {
    return;
  }

  // `showPopover()` throws on an already-open popover, and re-entry is real: tippy reuses the popper
  // element across show/hide cycles, so onMount can fire again on one still carrying the attribute.
  if (popper.matches(':popover-open')) {
    return;
  }

  popper.classList.add(TIPPY_TOP_LAYER_CLASS);
  popper.setAttribute('popover', 'manual');
  popper.showPopover();
}
